/**
 * debug-log — záchyt chyb, které by jinak skončily jen v konzoli prohlížeče.
 *
 * Motivace: na mobilní PWA není přístup k DevTools, takže když admin reportuje
 * „nefungují efekty na homepage", nemá jak zjistit, co padá. `installDebugCapture()`
 * obalí `console.error`/`console.warn` a poslouchá `window error` /
 * `unhandledrejection`, vše ukládá do omezeného in-memory bufferu. Zobrazení řeší
 * `DebugOverlay` (jen pro admina se zapnutým debug módem — viz `debug-mode.ts`).
 *
 * Záchyt je levný (jen kopie do pole + volání originálu), proto běží pro všechny —
 * tak má admin po zapnutí debug módu k dispozici i chyby z načtení stránky. Žádné
 * chování se nemění: originální `console.*` se vždy zavolá (DevTools/Sentry vidí vše).
 */

export type DebugLevel = "error" | "warn";

export interface DebugEntry {
  id: number;
  level: DebugLevel;
  /** Date.now() vzniku. */
  time: number;
  message: string;
  /** Stack trace, je-li k dispozici (z Error objektu). */
  stack?: string;
}

const MAX_ENTRIES = 200;
let entries: DebugEntry[] = [];
let seq = 0;
let installed = false;
const subs = new Set<() => void>();

function notify(): void {
  subs.forEach((f) => f());
}

export function getDebugEntries(): DebugEntry[] {
  return entries;
}

export function subscribeDebugLog(cb: () => void): () => void {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
}

export function clearDebugLog(): void {
  if (entries.length === 0) return;
  entries = [];
  notify();
}

function formatArg(a: unknown): string {
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (typeof a === "string") return a;
  if (typeof a === "number" || typeof a === "boolean" || a == null) return String(a);
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function firstStack(args: unknown[]): string | undefined {
  const err = args.find((a) => a instanceof Error) as Error | undefined;
  return err?.stack;
}

function push(level: DebugLevel, message: string, stack?: string): void {
  const entry: DebugEntry = { id: seq++, level, time: Date.now(), message, ...(stack ? { stack } : {}) };
  // Nová identita pole (kvůli useSyncExternalStore) + ořez na MAX_ENTRIES.
  entries = entries.length >= MAX_ENTRIES ? [...entries.slice(1), entry] : [...entries, entry];
  notify();
}

/** Idempotentně nainstaluje záchyt chyb. Volá se z `main.tsx` co nejdřív. */
export function installDebugCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);

  console.error = (...args: unknown[]): void => {
    push("error", args.map(formatArg).join(" "), firstStack(args));
    origError(...args);
  };
  console.warn = (...args: unknown[]): void => {
    push("warn", args.map(formatArg).join(" "), firstStack(args));
    origWarn(...args);
  };

  window.addEventListener("error", (e: ErrorEvent) => {
    const where = e.filename ? ` (${e.filename}:${e.lineno}:${e.colno})` : "";
    push("error", (e.message || "Script error") + where, e.error instanceof Error ? e.error.stack : undefined);
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const r: unknown = e.reason;
    const message = r instanceof Error ? `${r.name}: ${r.message}` : `Unhandled rejection: ${formatArg(r)}`;
    push("error", message, r instanceof Error ? r.stack : undefined);
  });
}
